import { useMemo, useState } from "react";
import type { ColumnDef } from "@tanstack/react-table";
import { Link, useNavigate } from "react-router-dom";
import { SendTestBox } from "./SendTestBox";
import { ammanStamp } from "../lib/dates";
import { canEditIssue, canSubmit } from "../lib/issue-store";
import type { IssueDetail, IssueEditable } from "../lib/segment-types";
import { DataTable } from "./DataTable";
import { DetailDrawer } from "./DetailDrawer";
import { IconBolt, IconDocument } from "./icons";
import {
  Bidi, EmptyState, Pill, PrimaryButton, SecondaryButton, StatusBadge, Surface, Tag,
} from "./primitives";

const PIPELINE = [
  { key: "draft", label: "Draft", hint: "Freelancer writing" },
  { key: "in_review", label: "In review", hint: "Editorial check" },
  { key: "approved", label: "Approved", hint: "Ready to send" },
  { key: "sent", label: "Sent", hint: "Performance tracked" },
] as const;


function issueTone(status: string): "success" | "info" | "warn" | "muted" {
  if (status === "sent") return "success";
  if (status === "approved" || status === "scheduled") return "info";
  if (status === "in_review") return "warn";
  return "muted";
}

function statusLabel(status: string) {
  return status.replace("_", " ");
}

export interface IssueActions {
  onUpdate: (id: string, fields: IssueEditable) => void;
  onDetach: (id: string, url: string) => void;
  onSubmit: (id: string) => void;
  onApprove: (id: string) => void;
  onRequestChanges: (id: string, comment: string, blockId?: string) => void;
}

export function IssueWorkspace({
  issues, selectedId, onSelect, actions, canApprove, compact,
}: {
  issues: IssueDetail[];
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  actions: IssueActions;
  canApprove: boolean;
  compact?: boolean;
}) {
  const [view, setView] = useState<"board" | "list">("board");
  const selected = issues.find((i) => i.id === selectedId) ?? null;
  const needsReview = issues.filter((i) => i.status === "in_review");
  const inPipeline = issues.filter((i) => i.status !== "sent").length;

  const columns = useMemo<ColumnDef<IssueDetail>[]>(() => [
    {
      accessorKey: "title", header: "Newsletter",
      cell: (c) => {
        const row = c.row.original;
        return (
          <button type="button" onClick={() => onSelect(row.id)} style={{
            border: "none", background: "none", padding: 0, cursor: "pointer",
            font: "inherit", textAlign: "left", color: "var(--c-t1)",
          }}>
            <div style={{ fontWeight: 600 }}>{row.title}</div>
            <div style={{ fontSize: "var(--ui-text-meta)", color: "var(--c-t3)", marginTop: 2 }}><Bidi>{row.subject}</Bidi></div>
          </button>
        );
      },
    },
    { accessorKey: "author", header: "Author" },
    { accessorKey: "articles", header: "Articles", meta: { numeric: true } },
    { accessorKey: "status", header: "Status",
      cell: (c) => {
        const s = c.getValue<string>();
        return <StatusBadge tone={issueTone(s)}>{statusLabel(s)}</StatusBadge>;
      } },
    { accessorKey: "open_rate", header: "Open rate", meta: { numeric: true },
      cell: (c) => {
        const v = c.getValue<number | null>();
        return v != null ? `${v}%` : "—";
      } },
  ], [onSelect]);

  return (
    <div style={{ display: "grid", gap: "var(--ui-space-4)" }}>
      {!compact && needsReview.length > 0 && (
        <div className="nl-alert">
          <IconBolt width={20} height={20} />
          <div>
            <div className="nl-alert-title">
              {needsReview.length === 1 ? "1 newsletter needs review" : `${needsReview.length} newsletters need review`}
            </div>
            <div className="nl-alert-body">
              {needsReview.map((i) => i.title).join(" · ")}
            </div>
          </div>
          <SecondaryButton onClick={() => onSelect(needsReview[0].id)}>Review now</SecondaryButton>
        </div>
      )}

      {!compact && (
      <Surface style={{ overflow: "hidden" }}>
        <div className="nl-stat-row">
          <div className="nl-stat-mini">
            <div className="nl-stat-mini-label">In pipeline</div>
            <div className="nl-stat-mini-value tabnum">{inPipeline}</div>
          </div>
          <div className="nl-stat-mini">
            <div className="nl-stat-mini-label">Awaiting review</div>
            {/* Neutral ink: a count is not good or bad news by itself. */}
            <div className="nl-stat-mini-value tabnum">{needsReview.length}</div>
          </div>
          <div className="nl-stat-mini">
            <div className="nl-stat-mini-label">Ready to send</div>
            <div className="nl-stat-mini-value tabnum">
              {issues.filter((i) => i.status === "approved").length}
            </div>
          </div>
          <div className="nl-stat-mini">
            <div className="nl-stat-mini-label">Sent (all time)</div>
            <div className="nl-stat-mini-value tabnum">{issues.filter((i) => i.status === "sent").length}</div>
          </div>
        </div>
      </Surface>
      )}

      <div className="nl-workspace">
        <Surface style={{ overflow: "hidden", minWidth: 0 }}>
          <div className="nl-toolbar">
            {!compact && (
              <>
                <span style={{ fontSize: "var(--ui-text-control)", color: "var(--c-t3)", fontWeight: 500 }}>View</span>
                <Pill active={view === "board"} onClick={() => setView("board")}>Board</Pill>
                <Pill active={view === "list"} onClick={() => setView("list")}>List</Pill>
              </>
            )}
            {compact && (
              <span style={{ fontSize: "var(--ui-text-control)", color: "var(--c-t3)", fontWeight: 500 }}>
                Drafts · In review · Approved · Sent
              </span>
            )}
          </div>

          {view === "board" ? (
            <IssueBoard issues={issues} selectedId={selectedId} onSelect={onSelect} />
          ) : (
            <div style={{ padding: "0 var(--ui-space-4) var(--ui-space-4)" }}>
              <DataTable data={issues} columns={columns} paginate={false} ariaLabel="Newsletters" />
            </div>
          )}
        </Surface>

        {selected && (
          <DetailDrawer label="Newsletter" onClose={() => onSelect(null)}>
            <IssueDetailPanel key={selected.id} issue={selected} actions={actions} canApprove={canApprove} />
          </DetailDrawer>
        )}
      </div>
    </div>
  );
}

function IssueBoard({
  issues, selectedId, onSelect,
}: {
  issues: IssueDetail[];
  selectedId: string | null;
  onSelect: (id: string) => void;
}) {
  const navigate = useNavigate();
  return (
    <div className="nl-pipeline" style={{ paddingBottom: "var(--ui-space-4)" }}>
      {PIPELINE.map((col) => {
        const colIssues = issues.filter((i) => i.status === col.key);
        return (
          <div key={col.key} className="nl-pipeline-col">
            <div className="nl-pipeline-col-head">
              <div className="nl-pipeline-label">{col.label}</div>
              <span className="nl-pipeline-count tabnum">{colIssues.length}</span>
            </div>
            {colIssues.length === 0 ? (
              <div className="nl-issue-chip empty-col">No issues here</div>
            ) : colIssues.map((i) => (
              // A div, not a button: the card carries its own Open control, and
              // a button inside a button is not valid markup.
              <div
                key={i.id}
                role="button"
                tabIndex={0}
                className={`nl-issue-chip${selectedId === i.id ? " selected" : ""}`}
                onClick={() => onSelect(i.id)}
                onDoubleClick={() => navigate(`/content/${i.id}`)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onSelect(i.id); }
                }}
              >
                <div style={{ fontWeight: 600, color: "var(--c-t1)", lineHeight: 1.3 }}>{i.title || "Untitled newsletter"}</div>
                <div style={{ fontSize: "var(--ui-text-meta)", color: "var(--c-t3)", marginTop: 3 }}>
                  <Bidi>{i.subject || "No subject yet"}</Bidi>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 6 }}>
                  <span style={{ fontSize: "var(--ui-text-meta)", color: "var(--c-t4)" }}>
                    {i.author} · {i.articles} articles
                  </span>
                  {i.open_rate != null && (
                    <span className="tabnum" style={{ fontSize: "var(--ui-text-meta)", fontWeight: 600, color: "var(--c-success-text)" }}>
                      {i.open_rate}%
                    </span>
                  )}
                </div>
                {i.next_action && i.status !== "sent" && (
                  <div style={{ fontSize: "var(--ui-text-meta)", color: "var(--c-warn-text)", marginTop: 6, fontWeight: 500 }}>
                    → {i.next_action}
                  </div>
                )}
                <button
                  type="button"
                  className="nl-issue-open"
                  title={i.status === "draft"
                    ? "Open in the newsletter designer"
                    : "View in the newsletter designer — not editable at this stage"}
                  onClick={(e) => { e.stopPropagation(); navigate(`/content/${i.id}`); }}
                >
                  {i.status === "draft" ? "Open" : "View"} <span aria-hidden>→</span>
                </button>
              </div>
            ))}
          </div>
        );
      })}
    </div>
  );
}

function IssueDetailPanel({ issue, actions, canApprove }: { issue: IssueDetail | null; actions: IssueActions; canApprove: boolean }) {
  const [reviewComment, setReviewComment] = useState("");
  const [reviewBlockId, setReviewBlockId] = useState("");

  if (!issue) {
    return (
      <div className="nl-detail-panel">
        <EmptyState
          title="Select a newsletter"
          body="Click any issue on the board to edit, attach articles, and move it through review."
          hint="Issues move Draft → Review → Approved → Sent."
        />
      </div>
    );
  }

  const editable = canEditIssue(issue);
  const submitReady = canSubmit(issue);

  return (
    <aside className="nl-detail-panel">
      <div className="nl-detail-panel-head">
        <StatusBadge tone={issueTone(issue.status)}>{statusLabel(issue.status)}</StatusBadge>
        {editable ? (
          <label className="nl-field" style={{ marginTop: "var(--ui-space-3)" }}>
            <span>Title</span>
            <input
              className="nl-input"
              value={issue.title}
              onChange={(e) => actions.onUpdate(issue.id, { title: e.target.value })}
              aria-label="Newsletter title"
            />
          </label>
        ) : (
          <div style={{ fontSize: "var(--ui-text-section)", fontWeight: 600, color: "var(--c-t1)", marginTop: "var(--ui-space-2)" }}>
            {issue.title || "Untitled newsletter"}
          </div>
        )}
        {editable ? (
          <label className="nl-field" style={{ marginTop: "var(--ui-space-2)" }}>
            <span>Subject line</span>
            <input
              className="nl-input"
              dir="auto"
              value={issue.subject}
              onChange={(e) => actions.onUpdate(issue.id, { subject: e.target.value })}
              placeholder="Arabic subject line"
              aria-label="Subject line"
            />
          </label>
        ) : (
          <div style={{ fontSize: "var(--ui-text-control)", color: "var(--c-t2)", marginTop: 4 }}>
            Subject: <Bidi>{issue.subject || "—"}</Bidi>
          </div>
        )}
        <div style={{ fontSize: "var(--ui-text-meta)", color: "var(--c-t3)", marginTop: 6 }}>
          {issue.author} · Updated {ammanStamp(issue.updated_at)}
        </div>

        {/* The designer used to be reachable only from the foot of this panel,
            below the article list, and only while the issue was a draft. An
            issue in review or already sent had no way in at all. */}
        <Link to={`/content/${issue.id}`} className="nl-open-designer">
          {editable ? "Open designer" : "Open designer (read-only)"}
          <span aria-hidden>→</span>
        </Link>
      </div>

      <div className="nl-detail-panel-body">
        {issue.next_action && issue.status !== "sent" && (
          <div style={{
            padding: "var(--ui-space-3)",
            background: "var(--c-tint-amber)",
            borderRadius: "var(--ui-control-radius)",
            fontSize: "var(--ui-text-control)",
          }}>
            <strong style={{ color: "var(--c-warn-text)" }}>Next step:</strong> {issue.next_action}
          </div>
        )}

        {editable ? (
          <label className="nl-field">
            <span>Editor notes</span>
            <textarea
              className="nl-textarea"
              value={issue.notes ?? ""}
              onChange={(e) => actions.onUpdate(issue.id, { notes: e.target.value })}
              rows={3}
            />
          </label>
        ) : issue.notes ? (
          <div>
            <div className="nl-stat-mini-label">Editor notes</div>
            <p style={{ fontSize: "var(--ui-text-control)", color: "var(--c-t2)", margin: "4px 0 0", lineHeight: 1.5, whiteSpace: "pre-wrap" }}>
              {issue.notes}
            </p>
            {issue.reviewer && (
              <div style={{ fontSize: "var(--ui-text-meta)", color: "var(--c-t3)", marginTop: 4 }}>
                Reviewer: {issue.reviewer}
              </div>
            )}
          </div>
        ) : null}

        <div>
          <div className="nl-stat-mini-label">Articles ({issue.articles})</div>
          <div className="nl-article-grid" style={{ padding: "var(--ui-space-2) 0 0" }}>
            {(issue.articles_list ?? []).map((a) => (
              <div key={a.url} className="nl-article-card">
                <div className="nl-article-thumb" aria-hidden><IconDocument /></div>
                <div style={{ minWidth: 0 }}>
                  <a href={a.url} target="_blank" rel="noopener noreferrer" style={{ fontWeight: 500, fontSize: "var(--ui-text-control)", lineHeight: 1.3, color: "inherit", textDecoration: "none" }}>
                    <Bidi>{a.title}</Bidi>
                  </a>
                  {a.category && (
                    <div style={{ fontSize: "var(--ui-text-meta)", color: "var(--c-t3)", marginTop: 2 }}>{a.category}</div>
                  )}
                </div>
                {editable ? (
                  <SecondaryButton onClick={() => actions.onDetach(issue.id, a.url)} title="Remove from this draft">
                    Remove
                  </SecondaryButton>
                ) : (
                  <Tag tone="teal">Open</Tag>
                )}
              </div>
            ))}
            {(!issue.articles_list || issue.articles_list.length === 0) && (
              <div style={{ fontSize: "var(--ui-text-control)", color: "var(--c-t3)" }}>
                No articles yet. Add them in the designer.
              </div>
            )}
          </div>
        </div>

        {issue.open_rate != null && (
          <div>
            <div className="nl-stat-mini-label">Performance</div>
            <div className="tabnum" style={{ fontSize: 28, fontWeight: 700, color: "var(--c-success-text)", marginTop: 4 }}>
              {issue.open_rate}% open rate
            </div>
          </div>
        )}

        {issue.status === "approved" && (
          <div className="nl-ready-campaign">
            <div className="nl-stat-mini-label">Ready to send</div>
            <p>Send it from Broadcasts to everyone who follows its topics, now or at a set time.</p>
          </div>
        )}

        {issue.status === "in_review" && (
          <>
            <label className="nl-field">
              <span>Request-changes comment</span>
              <textarea
                className="nl-textarea"
                value={reviewComment}
                onChange={(e) => setReviewComment(e.target.value)}
                placeholder="What should the author fix?"
                rows={2}
              />
            </label>
            {(issue.blocks ?? []).length > 0 && (
              <label className="nl-field">
                <span>Attach to block <em>(optional)</em></span>
                <select className="nl-builder-select" value={reviewBlockId} onChange={(e) => setReviewBlockId(e.target.value)}>
                  <option value="">Whole newsletter</option>
                  {(issue.blocks ?? []).map((b, i) => (
                    <option key={b.id} value={b.id}>
                      {b.type} {i + 1}{b.text ? ` — ${b.text.slice(0, 40)}` : ""}
                    </option>
                  ))}
                </select>
              </label>
            )}
          </>
        )}

        <SendTestBox issue={issue} />

        <div className="nl-action-row">
          {issue.status === "draft" && (
            <>
              <SecondaryButton
                disabled={!submitReady}
                title={submitReady ? "Send to editorial review" : "Need a title, subject, and content"}
                onClick={() => actions.onSubmit(issue.id)}
              >
                Submit for review
              </SecondaryButton>
            </>
          )}
          {issue.status === "in_review" && canApprove && (
            <>
              <PrimaryButton onClick={() => { setReviewComment(""); actions.onApprove(issue.id); }}>
                Approve
              </PrimaryButton>
              <SecondaryButton onClick={() => {
                actions.onRequestChanges(issue.id, reviewComment, reviewBlockId || undefined);
                setReviewComment("");
                setReviewBlockId("");
              }}>
                Request changes
              </SecondaryButton>
            </>
          )}
          {issue.status === "in_review" && !canApprove && (
            <p style={{ margin: 0, fontSize: "var(--ui-text-meta)", color: "var(--c-t3)" }}>Waiting for an editor to approve.</p>
          )}
          {issue.status === "approved" && (
            <Link to="/broadcasts" style={{ textDecoration: "none" }}>
              <PrimaryButton title="Send this newsletter to its topics' followers">Send as a broadcast</PrimaryButton>
            </Link>
          )}
          {issue.status === "sent" && (
            <Link to="/broadcasts" style={{ textDecoration: "none" }}>
              <SecondaryButton>See results in Broadcasts</SecondaryButton>
            </Link>
          )}
        </div>
      </div>
    </aside>
  );
}

